// Feature-flag registry for dashboard-web.
//
// One source of truth for every WIP / role-gated piece of UI. A flag
// turns ON for a session when:
//   1. Its `default` is true, OR
//   2. The signed-in user has any role in `requireRoles`, OR
//   3. The signed-in user's email matches an entry in `allowEmails`
//      (escape hatch for one-off staff overrides without minting a
//      new Zitadel role just for previewing).
//
// To gate something:
//   - Inline:    `<FeatureFlag flag="x">…</FeatureFlag>`
//   - Hook:      `const on = useFeatureFlag('x')`
//   - Nav item:  add `flag: 'x'` to the nav entry; AppShell filters
//                hidden items automatically.
//
// To add a new flag:
//   1. Append a row to FLAGS below with a stable kebab-case key
//   2. Use it wherever you need to gate
//   3. Flip `default: true` once the feature is GA
//
// Resolution is pure (no async, no network) so it can be called from
// any render path without effect plumbing.

export interface FeatureFlagDef {
  /** Stable id. Don't reuse keys when retiring a flag — delete the row. */
  key:           string;
  /** Human label for design reviews / docs. Not shown to end users. */
  label:         string;
  /** Free-text description for design reviews / docs. */
  description?:  string;
  /** When true, the flag is ON for everyone. Use to GA a feature
   *  without touching every call site. */
  default?:      boolean;
  /** Project role keys that unlock the flag (any-of). Source: the
   *  JWT's `roles` / `urn:zitadel:iam:org:project:roles` claims. */
  requireRoles?: string[];
  /** Per-email overrides — handy for previewing features without
   *  minting a Zitadel role. Lower-cased before comparison. */
  allowEmails?:  string[];
  /** If true, hide entirely when not authenticated (vs treating the
   *  user as a "logged-out role"). Default true. */
  authRequired?: boolean;
}

// Build-time env signal for backend readiness of the AlertsEngine.
// Surfaced as a flag default so call sites query a single resolver
// instead of branching on env vars directly.
const ALERTS_ENGINE_READY: boolean =
  ((import.meta as any).env?.VITE_ALERTS_ENABLED ?? '').toLowerCase() === 'true';

/** Centralised registry. Add rows here; never inline new keys
 *  elsewhere. */
export const FLAGS = {
  // ── Routes / pages ───────────────────────────────────────────────
  'alerts-page': {
    key:           'alerts-page',
    label:         'Alerts page',
    description:   'Surface /alerts in the sidebar and render the rule editor instead of the under-construction stub. Admin-only while the AlertsEngine is in preview.',
    requireRoles:  ['laser_admin'],
  },
  'sensor-mapping': {
    key:           'sensor-mapping',
    label:         'Sensor Mapping page',
    description:   'Surface /sensors in the sidebar and render the sensor catalog + per-company display-name editor (issue #4). Admin-only while WIP.',
    requireRoles:  ['laser_admin'],
  },
  'pdm-page': {
    key:           'pdm-page',
    label:         'PdM page',
    description:   'Surface /pdm (predictive-maintenance reports + score trend, issue #11). Per-user flag granted to paying companies\' users via the admin Users panel; ALSO enforced server-side (api 404s /v1/pdm/* without it) and combined with the company subscription (403 when disabled).',
    requireRoles:  ['laser_admin'],
  },

  // ── Backend-readiness signals (env-driven) ───────────────────────
  'alerts-engine': {
    key:           'alerts-engine',
    label:         'Alerts engine (backend)',
    description:   'AlertsEngine on the api side accepts live rule writes. Until ON, the editor is read-only / local-only.',
    default:       ALERTS_ENGINE_READY,
  },

  // ── Admin scope toggles ──────────────────────────────────────────
  'admin-cross-company': {
    key:           'admin-cross-company',
    label:         'Admin: cross-company data scope',
    description:   'Stream / query data across all companies. Admin-only — non-admins are pinned to their own company_slug regardless.',
    requireRoles:  ['laser_admin'],
  },

  // ── WIP / preview features ───────────────────────────────────────
  'wip-fleet-overview': {
    key:           'wip-fleet-overview',
    label:         'WIP: Fleet overview',
    description:   'Cross-machine roll-up view. Behind a preview gate while we iterate on the layout.',
    allowEmails:   ['ben@phos.solutions', 'ben@phos-ind.com'],
  },
  'wip-predictive-maintenance': {
    key:           'wip-predictive-maintenance',
    label:         'WIP: Predictive maintenance alerts',
    description:   'ML-driven anomaly + drift signals surfaced on the dashboard. Internal preview.',
    allowEmails:   ['ben@phos.solutions', 'ben@phos-ind.com'],
  },
  'wip-historian-export': {
    key:           'wip-historian-export',
    label:         'WIP: Historian CSV/JSON export',
    description:   'Export the visible history slice as CSV or JSON. Internal preview until quota story is settled.',
    allowEmails:   ['ben@phos.solutions', 'ben@phos-ind.com'],
  },
  'mcp-access': {
    key:           'mcp-access',
    label:         'MCP access (Claude connection)',
    description:   'Customer-facing MCP surface. No UI behind it yet — the flag reserves the gate for the future self-service page; the api 404s /v1/mcp/entitlement without it.',
    requireRoles:  ['laser_admin'],
  },
} as const satisfies Record<string, FeatureFlagDef>;

export type FeatureFlagKey = keyof typeof FLAGS;

// Server-served definitions (the central registry) override the compiled
// list above, which remains the offline/first-paint fallback. Keys unknown
// to this build still resolve, so new flags gate without a frontend release.
let dynamicDefs: Map<string, FeatureFlagDef> | null = null;

export function setFlagDefs(defs: {
  key: string; label: string; description?: string; default?: boolean;
  require_roles?: string[]; allow_emails?: string[]; auth_required?: boolean;
}[]): void {
  dynamicDefs = new Map(defs.map((d) => [d.key, {
    key: d.key, label: d.label, description: d.description,
    default: d.default, requireRoles: d.require_roles,
    allowEmails: d.allow_emails, authRequired: d.auth_required,
  }]));
}

export function flagDef(key: string): FeatureFlagDef | undefined {
  return dynamicDefs?.get(key) ?? (FLAGS as Record<string, FeatureFlagDef>)[key];
}

export function allFlagKeys(): string[] {
  return dynamicDefs ? [...dynamicDefs.keys()] : Object.keys(FLAGS);
}

interface AuthContext {
  email: string | null;
  roles: string[];
  isAuthed: boolean;
  /** Per-user override list from /v1/users/me. Forces the listed
   *  flag keys ON regardless of the registry's default / role / email
   *  rules. Admins (laser_admin) bypass this and get every flag
   *  anyway. */
  flagOverrides?: string[];
}

/** Pure resolver. Exposed for tests, nav filters, and anywhere a hook
 *  is inconvenient. Hook + component wrap this.
 *
 *  Resolution order:
 *    1. authRequired and not authed                 → OFF
 *    2. user has 'laser_admin' role                 → ON (admin = all)
 *    3. flag key in user's override list            → ON
 *    4. flag.default === true                       → ON
 *    5. flag.requireRoles intersects user roles     → ON
 *    6. flag.allowEmails matches user email         → ON
 *    7. otherwise                                   → OFF
 *
 *  We widen the lookup to FeatureFlagDef because `satisfies` narrows
 *  each value to its literal shape — meaning rows without
 *  `requireRoles` lose the property from their type and fail the
 *  union-access check. Widening at the lookup boundary keeps the
 *  registry strongly typed for authors while letting the resolver
 *  read optional fields uniformly. */
export function evaluateFlag(key: FeatureFlagKey, ctx: AuthContext): boolean {
  const def = flagDef(key);
  if (!def) return false;
  if ((def.authRequired ?? true) && !ctx.isAuthed) return false;
  // Admins get everything. This is policy, not configuration — admins
  // are expected to see (and test) every feature the product can show.
  // Individual flag definitions don't need `requireRoles: ['laser_admin']`
  // anymore for this purpose; that field stays useful for non-admin
  // roles (future operator-only flags etc).
  if (ctx.roles.includes('laser_admin')) return true;
  if (ctx.flagOverrides && ctx.flagOverrides.includes(key)) return true;
  if (def.default === true) return true;
  if (def.requireRoles && def.requireRoles.some((r) => ctx.roles.includes(r))) return true;
  if (def.allowEmails && ctx.email != null) {
    const e = ctx.email.toLowerCase();
    if (def.allowEmails.some((a) => a.toLowerCase() === e)) return true;
  }
  return false;
}
